"use client";

import React, { useMemo } from "react";
import { Button } from "react-bootstrap";
import { usePathname } from "next/navigation";
import punycode from "punycode/";
import styles from "./DomainDynamic.module.css"
interface DomainData {
  name: string;
  maincat?: string;
  extension: string;
  pricing: any;
}

interface Domain {
  domains: DomainData[];
}

interface DomainButton {
  text: string;
  link: string;
}

/* ---------------- HELPERS ---------------- */

// normalize ANY extension (single + multi-level) to unicode
const normalizeExt = (v: string = "") => {
  try {
    return v
      .replace(/^\./, "")
      .split(".")
      .map((p) => punycode.toUnicode(p))
      .join(".")
      .toLowerCase();
  } catch {
    return v.replace(/^\./, "").toLowerCase();
  }
};

const getBestRegisterPrice = (pricing: any) => {
  const r = pricing?.domainregister;
  return r?.msetupfee ?? r?.qsetupfee ?? r?.ssetupfee ?? null;
};

/* ---------------- COMPONENT ---------------- */

const DomainDynamic: React.FC<{ domain: Domain }> = ({ domain }) => {
  const pathname = usePathname();
  const slug = decodeURIComponent(pathname.split("/").pop() || "");

  const splitIndex = slug.lastIndexOf("-.");
  const countrySlug = slug.slice(0, splitIndex);
  const extPart = slug.slice(splitIndex + 2);

  const activeExt = normalizeExt(extPart);
  const currentKey = countrySlug.replace(/_/g, " ").toLowerCase();

  /* ---------- GROUP DOMAINS (FOR UI SECTIONS) ---------- */
  const countryDomains = useMemo(() => {
    return domain.domains.filter((d) => {
      if (currentKey === "idn gtld") {
        return d.maincat?.toLowerCase() === "idn gtld";
      }
      return d.name?.toLowerCase() === currentKey;
    });
  }, [domain.domains, currentKey]);

  /* ---------- ACTIVE DOMAIN (FINAL GUARANTEED FIX) ---------- */
  const activeDomain = useMemo(() => {
    // 1️⃣ try within current group
    const inGroup = countryDomains.find(
      (d) => normalizeExt(d.extension) === activeExt
    );
    if (inGroup) return inGroup;

    // 2️⃣ fallback: search ALL domains by extension
    return domain.domains.find(
      (d) => normalizeExt(d.extension) === activeExt
    );
  }, [countryDomains, domain.domains, activeExt]);

  const clientUrl =
    (process.env.NEXT_PUBLIC_API_BASE_URL ||
      "https://clients.temok.com") +
    "/cart.php?a=add&domain=register";

  /* ---------- POPULAR DOMAINS ---------- */
  const popularDomains: DomainButton[] = useMemo(() => {
    return countryDomains
      .map((d) => {
        const price = getBestRegisterPrice(d.pricing);
        if (!price) return null;

        const ext = normalizeExt(d.extension);
        return {
          text: `.${ext} $${price}`,
          link: `/country-domains/${countrySlug}-.${ext}`,
        };
      })
      .filter(Boolean)
      .slice(0, 8) as DomainButton[];
  }, [countryDomains, countrySlug]);

  /* ---------- RELATED DOMAINS ---------- */
  const relatedDomains: DomainButton[] = useMemo(() => {
    return countryDomains
      .filter((d) => normalizeExt(d.extension) !== activeExt)
      .slice(0, 10)
      .map((d) => {
        const ext = normalizeExt(d.extension);
        const price = getBestRegisterPrice(d.pricing);
        return {
          text: `.${ext}${price ? ` $${price}` : ""}`,
          link: `/country-domains/${countrySlug}-.${ext}`,
        };
      });
  }, [countryDomains, activeExt, countrySlug]);

  const base =
    process.env.NEXT_PUBLIC_API_BASE_URL ||
    "https://clients.temok.com";

  const domainOptions: DomainButton[] = [
    { text: "Register Domain", link: `${base}/cart.php?a=add&domain=register` },
    { text: "Renew Domain", link: `${base}/cart.php?gid=renewals` },
    { text: "Transfer Domain", link: `${base}/cart.php?a=add&domain=transfer` },
  ];

  const renderButtons = (buttons: DomainButton[]) =>
    buttons.map((b, i) => (
      <Button
        key={i}
        variant="outline-light"
        size="lg"
        className="m-1"
        onClick={() => (window.location.href = b.link)}
      >
        {b.text}
      </Button>
    ));

  /* ---------------- RENDER ---------------- */

  return (
    <>
      <div className="searchInputBg text-center py-5">
        <h1 className="fw-bold">
          Register your <span className="textOrange">.{activeExt}</span> Domain
        </h1>

        {/* 🔍 DOMAIN SEARCH */}
        <div className="d-flex justify-content-center mt-4">
          <div className="col-lg-8 col-md-10 col-12">
            <form action={clientUrl} method="post" >
              <div className={`${styles.searchBox}  align-items-center input-group bg-white p-2 rounded-3`}>
                <input
                  type="text"
                  name="query" 
                  className={`${styles.input} form-control form-control-lg rounded-2`}
                  placeholder="Search your perfect domain name"
                />
                <button className="btn btn-primary btnfill0 ">
                  Search 
                </button>
              </div>
            </form>
          </div>
        </div>

 

        {/* PRICING */}
        <div className="row justify-content-center my-4">
          <div className="col-md-8">
            <div className="row">
              <div className="col-md-4">
                <div className="domClass">
                  <p>Registration</p>
                  <h4>
                    ${getBestRegisterPrice(activeDomain?.pricing) ?? "N/A"}/yr
                  </h4>
                </div>
              </div>
              <div className="col-md-4">
                <div className="domClass">
                  <p>Renewal</p>
                  <h4>
                    ${activeDomain?.pricing?.domainrenew?.msetupfee ?? "N/A"}/yr
                  </h4>
                </div>
              </div>
              <div className="col-md-4">
                <div className="domClass">
                  <p>Transfer</p>
                  <h4>
                    ${activeDomain?.pricing?.domaintransfer?.msetupfee ?? "N/A"}/yr
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center fs-3 mt-4">
          Temok is proud to offer registration services for
          <br />.{activeExt} domain names.
        </p>
      </div>

      {/* 🔥 NAV SECTION (UNCHANGED) */}
      <div className="navBg py-5">
        <div className="container">
          <h3 className="text-white">Popular Business Domains</h3>
          <div>{renderButtons(popularDomains)}</div>

          <h3 className="text-white mt-5">Domain Name Options</h3>
          <div>{renderButtons(domainOptions)}</div>

          <h3 className="text-white mt-5">Related Domain Extensions</h3>
          <div>
            {relatedDomains.length ? (
              renderButtons(relatedDomains)
            ) : (
              <p className="text-white">No related domains found.</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DomainDynamic;
